import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import {
  createClassifier,
  deleteSingleCassifier,
  loadClassifier,
  saveClassifier,
  ZClassifier,
  ZClassifierCard,
  zClassifierCard,
} from "src/business/classifier";
import { dataParser } from "src/common/dataParser";
import { mdmBreadcrumbs } from "src/common/mdmBreadcrumbs";
import { RemoteData } from "src/common/RemoteData";

export const classifierCardStore = makeAutoObservable({
  id: "",
  get isNew(): boolean {
    return this.id === "new";
  },
  get title(): string {
    if (this.isNew) return "Новый классификатор";
    return this.data.status === "ready" ? this.data.result.name : "...";
  },
  data: { status: "none" } as RemoteData<ZClassifier>,
  setData(newData: RemoteData<ZClassifier>) {
    this.data = newData;
  },
  async init(id: string) {
    this.id = id;
    this.setChanged(false);
    if (this.isNew) {
      this.setData({
        status: "ready",
        result: {
          id: "",
          displayId: 0,
          name: "",
        },
      });
    } else {
      try {
        this.setData({ status: "wait" });
        const result = await loadClassifier(id);
        this.setData({ status: "ready", result });
      } catch (error) {
        this.setData({ status: "error", error });
      }
    }
  },
  saving: false,
  setSaving(value: boolean) {
    this.saving = value;
  },
  async updateValues(
    rawValues: unknown,
    task: (values: ZClassifierCard) => Promise<ZClassifier>,
  ): Promise<ZClassifier> {
    const { data } = this;
    try {
      this.setSaving(true);
      if (data.status !== "ready") throw Error("Нет данных");
      const values = dataParser(
        () => zClassifierCard.parse(rawValues),
        "Получение данных из формы",
      );
      const result = await task(values);
      this.setData({ status: "ready", result });
      return result;
    } finally {
      this.setSaving(false);
    }
  },
  save(rawValues: unknown): Promise<ZClassifier> {
    return this.updateValues(rawValues, (values) =>
      saveClassifier(this.id, values),
    );
  },
  create(rawValues: unknown): Promise<ZClassifier> {
    return this.updateValues(rawValues, createClassifier);
  },
  async delete(id: string) {
    await deleteSingleCassifier(id);
    this.setChanged(false);
  },
  initialValues: null as ZClassifierCard | null,
  setInitialValues(values: ZClassifierCard | null) {
    this.initialValues = values;
  },
  changed: false,
  setChanged(value: boolean) {
    this.changed = value;
  },
  get crumbs(): BreadcrumbItem[] {
    const { data, isNew } = this;
    const name = isNew
      ? "Новый классификатор"
      : (data.status === "ready" && data.result.name) || "...";
    return mdmBreadcrumbs([
      { type: "ObjAndSpecs" },
      { type: "AdminClassifiers" },
      { type: "Name", name },
    ]);
  },
});
